import { useCallback, useEffect, useState } from "react";
import { apiClient, ApiError } from "@lib/apiClient";

export interface AuthUser {
  id: string;
  email: string;
  role: "SUPERADMIN" | "ADMIN" | "USER";
  approved?: boolean;
  displayName?: string;
  /** Whether TOTP two-factor auth is enabled (returned by GET /auth/me). */
  mfaEnabled?: boolean;
}

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("aria.token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const u = await apiClient.get<AuthUser>("/auth/me");
      setUser(u);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        localStorage.removeItem("aria.token");
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback((token: string, u: AuthUser) => {
    localStorage.setItem("aria.token", token);
    setUser(u);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("aria.token");
    setUser(null);
    // Purge offline-cached user data (history, messages, contacts) so it can't
    // be read by the next person on a shared device.
    if (typeof caches !== "undefined") {
      void caches.delete("aria-api-cache");
    }
  }, []);

  return { user, loading, refresh, login, logout };
}